import {
  SCHEMA_VERSION,
  StorageQuotaExceededError,
  StorageSchemaTooNewError,
  type CheckInTemplate,
  type Collection,
  type Entry,
  type EntryCollection,
  type EpochRange,
  type SettingsStore,
  type Storage,
  type Tag,
  type Track,
} from '@anytracker/core'
import Dexie, { type Table } from 'dexie'

import {
  DB_NAME,
  DB_VERSION,
  STORE_NAMES,
  defineSchema,
  fromStored,
  toStored,
  type AnytrackerDb,
  type StoredEntry,
  type StoredSetting,
} from './schema.js'

/** Reserved settings key holding the domain schema version. */
const SCHEMA_VERSION_KEY = '__schemaVersion'

/**
 * Turns a browser storage failure into the port's typed error.
 *
 * Every write goes through this. A raw `QuotaExceededError` surfaces as an
 * unhandled rejection, which presents to the user as a check-in that silently
 * did not save — the worst failure available to an app whose purpose is
 * producing evidence.
 */
async function mapErrors<T>(work: () => Promise<T>): Promise<T> {
  try {
    return await work()
  } catch (error) {
    if (isQuotaError(error)) {
      throw new StorageQuotaExceededError(
        'There is no room left to save on this device.',
        { cause: error },
      )
    }
    throw error
  }
}

function isQuotaError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false
  }
  // Dexie wraps the DOMException, and browsers disagree on the name: Chrome and
  // Firefox use QuotaExceededError, older WebKit used the legacy numeric code
  // under a different name. Match on both the Dexie class name and the
  // underlying one rather than picking a winner.
  const names = new Set([error.name, (error.cause as Error | undefined)?.name])
  return (
    names.has('QuotaExceededError') ||
    names.has('DexieQuotaExceededError') ||
    error instanceof Dexie.QuotaExceededError
  )
}

class TableCollection<T> implements Collection<T> {
  constructor(protected readonly table: Table<T, string>) {}

  async get(id: string): Promise<T | undefined> {
    return mapErrors(() => this.table.get(id))
  }

  async list(): Promise<T[]> {
    return mapErrors(() => this.table.toArray())
  }

  async put(item: T): Promise<void> {
    await mapErrors(() => this.table.put(item))
  }

  async putMany(items: readonly T[]): Promise<void> {
    if (items.length === 0) {
      return
    }
    await mapErrors(() => this.table.bulkPut([...items]))
  }

  async remove(id: string): Promise<void> {
    // Deleting a missing key is not an error in IndexedDB, which is what the
    // port promises too.
    await mapErrors(() => this.table.delete(id))
  }

  async count(): Promise<number> {
    return mapErrors(() => this.table.count())
  }

  async clear(): Promise<void> {
    await mapErrors(() => this.table.clear())
  }
}

/**
 * The entry collection, where the indexes actually get used.
 *
 * Every method here goes through `where(...)` on an indexed path. Rewriting one
 * as `.filter(...)` would still return correct results and would quietly turn a
 * seek into a scan of every entry ever recorded — which is why the tests time
 * these against a full scan rather than only checking the rows come back.
 */
class DexieEntries implements EntryCollection {
  // Deliberately not a subclass of `TableCollection<StoredEntry>`: every read
  // here returns `Entry`, not the stored shape, so inheriting would mean
  // overriding half the methods with incompatible return types.
  constructor(private readonly table: Table<StoredEntry, string>) {}

  async get(id: string): Promise<Entry | undefined> {
    const record = await mapErrors(() => this.table.get(id))
    return record === undefined ? undefined : fromStored(record)
  }

  async list(): Promise<Entry[]> {
    // Sorted by instant, like every other listing here, so callers never depend
    // on insertion order by accident.
    const records = await mapErrors(() => this.table.orderBy('at.epochMs').toArray())
    return records.map(fromStored)
  }

  async put(item: Entry): Promise<void> {
    await mapErrors(() => this.table.put(toStored(item)))
  }

  async putMany(items: readonly Entry[]): Promise<void> {
    if (items.length === 0) {
      return
    }
    await mapErrors(() => this.table.bulkPut(items.map(toStored)))
  }

  async remove(id: string): Promise<void> {
    await mapErrors(() => this.table.delete(id))
  }

  async count(): Promise<number> {
    return mapErrors(() => this.table.count())
  }

  async clear(): Promise<void> {
    await mapErrors(() => this.table.clear())
  }

  async listByRange(range: EpochRange): Promise<Entry[]> {
    const records = await mapErrors(() =>
      this.table
        // Inclusive at both ends — start-of-day and end-of-day queries depend
        // on it, and an exclusive upper bound would drop the last entry of
        // every day.
        .where('at.epochMs')
        .between(range.fromEpochMs, range.toEpochMs, true, true)
        .toArray(),
    )
    return sorted(records)
  }

  async listByTrack(trackId: string, range?: EpochRange): Promise<Entry[]> {
    return this.#byIndex('trackIds', trackId, range)
  }

  async listByTag(tagId: string, range?: EpochRange): Promise<Entry[]> {
    return this.#byIndex('tagIds', tagId, range)
  }

  async listByTemplate(templateId: string, range?: EpochRange): Promise<Entry[]> {
    return this.#byIndex('templateId', templateId, range)
  }

  /*
   * Both answered from the `at.epochMs` index, reading one row.
   *
   * `.first()` and `.last()` on an ordered collection open a cursor and take a
   * single record; they do not fetch the collection. That matters because "all
   * time" is the default range and is resolved on every page load.
   */
  async earliest(): Promise<Entry | undefined> {
    return mapErrors(() => this.table.orderBy('at.epochMs').first())
  }

  async latest(): Promise<Entry | undefined> {
    return mapErrors(() => this.table.orderBy('at.epochMs').last())
  }

  /**
   * Seeks on the index, then narrows by range in memory.
   *
   * IndexedDB has no compound index over a multi-entry key and a range, so the
   * alternative would be scanning by instant and filtering by track. Seeking
   * the far more selective index first means the in-memory filter runs over one
   * track's entries rather than the whole history.
   */
  async #byIndex(
    index: 'trackIds' | 'tagIds' | 'templateId',
    value: string,
    range?: EpochRange,
  ): Promise<Entry[]> {
    const records = await mapErrors(() => this.table.where(index).equals(value).toArray())
    const withinRange =
      range === undefined
        ? records
        : records.filter(
            (record) =>
              record.at.epochMs >= range.fromEpochMs &&
              record.at.epochMs <= range.toEpochMs,
          )
    return sorted(withinRange)
  }
}

/** Ascending by instant, with id as a tie-break so the order is total. */
function sorted(records: readonly StoredEntry[]): Entry[] {
  return [...records]
    .sort((a, b) => a.at.epochMs - b.at.epochMs || a.id.localeCompare(b.id))
    .map(fromStored)
}

class DexieSettings implements SettingsStore {
  constructor(private readonly table: Table<StoredSetting, string>) {}

  async get(key: string): Promise<string | undefined> {
    return mapErrors(async () => (await this.table.get(key))?.value)
  }

  async set(key: string, value: string): Promise<void> {
    await mapErrors(() => this.table.put({ key, value }))
  }

  async remove(key: string): Promise<void> {
    await mapErrors(() => this.table.delete(key))
  }

  async all(): Promise<Record<string, string>> {
    const rows = await mapErrors(() => this.table.toArray())
    return Object.fromEntries(
      rows
        .filter((row) => row.key !== SCHEMA_VERSION_KEY)
        .map((row) => [row.key, row.value]),
    )
  }
}

export class DexieStorage implements Storage {
  readonly tracks: Collection<Track>
  readonly tags: Collection<Tag>
  readonly templates: Collection<CheckInTemplate>
  readonly entries: EntryCollection
  readonly settings: SettingsStore

  constructor(private readonly db: AnytrackerDb) {
    this.tracks = new TableCollection(db.tracks)
    this.tags = new TableCollection(db.tags)
    this.templates = new TableCollection(db.templates)
    this.entries = new DexieEntries(db.entries)
    this.settings = new DexieSettings(db.settings)
  }

  /**
   * All-or-nothing, delegated to IndexedDB's own transactions.
   *
   * `work` receives `this` rather than a differently-wired storage, because
   * Dexie binds the transaction to the surrounding async scope: table
   * operations inside the callback join it automatically. Handing back the same
   * object is what lets the conformance suite drive both implementations
   * through one interface.
   *
   * The caveat that matters: **do not await a non-Dexie promise inside `work`.**
   * Doing so leaves Dexie's async zone, and subsequent operations silently run
   * outside the transaction — committing when they should have rolled back.
   */
  async transaction<T>(work: (storage: Storage) => Promise<T>): Promise<T> {
    return mapErrors(() =>
      this.db.transaction('rw', STORE_NAMES as unknown as string[], async () =>
        work(this),
      ),
    )
  }

  async getSchemaVersion(): Promise<number> {
    const stored = await this.db.settings.get(SCHEMA_VERSION_KEY)
    // Absent means a database this build just created.
    return stored === undefined ? SCHEMA_VERSION : Number(stored.value)
  }

  async setSchemaVersion(version: number): Promise<void> {
    await mapErrors(() =>
      this.db.settings.put({ key: SCHEMA_VERSION_KEY, value: String(version) }),
    )
  }
}

/**
 * Opens the database, refusing one written by a newer build.
 *
 * The refusal is the point. A user on a stale cached build that read a newer
 * database would drop fields it does not understand and write the result
 * back — silently destroying data the newer build recorded. There is no server
 * copy to restore from (ADR-0003), so this fails closed and says why.
 *
 * IndexedDB enforces the structural half itself: opening at a lower version
 * than the stored one raises `VersionError`. The domain schema version is
 * checked separately because the two move independently — a data-format change
 * need not change the stores or indexes.
 */
export async function openDatabase(name = DB_NAME): Promise<DexieStorage> {
  const db = new Dexie(name) as AnytrackerDb
  defineSchema(db)

  try {
    await db.open()
  } catch (error) {
    // Some engines reject outright rather than opening. Either path must lead
    // to the same refusal, so this is a second route to it and not the only
    // one — see the `verno` check below.
    if (error instanceof Dexie.VersionError) {
      throw new StorageSchemaTooNewError(DB_VERSION + 1, DB_VERSION)
    }
    throw error
  }

  /**
   * Dexie does not fail on a newer database — it adapts to it.
   *
   * Declaring version 1 against a store written at version 2 opens in dynamic
   * schema mode and succeeds, so `VersionError` alone would let a stale cached
   * build read a database it does not understand, drop the fields it has no
   * schema for, and write the result back.
   *
   * The version to compare is `idbdb.version`, not `verno`: `verno` reports
   * what *this build declared*, so it reads 1 whatever is on disk. Dexie stores
   * the IndexedDB version as its own version × 10, leaving room for the
   * intermediate upgrade steps it runs between two declared versions.
   */
  const onDisk = (db.backendDB()?.version ?? DB_VERSION * 10) / 10
  if (onDisk > DB_VERSION) {
    db.close()
    throw new StorageSchemaTooNewError(onDisk, DB_VERSION)
  }

  const storage = new DexieStorage(db)
  const found = await storage.getSchemaVersion()
  if (found > SCHEMA_VERSION) {
    db.close()
    throw new StorageSchemaTooNewError(found, SCHEMA_VERSION)
  }

  return storage
}
